import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TwoPaneLayout } from '../components/TwoPaneLayout';
import { requestPasswordReset } from '../lib/apiClient';

/**
 * Forgot-password page. Email input → POST /auth/forgot-password →
 * "if an account exists, we sent a 6-digit code." Then the user follows
 * the email instructions back to /reset-password (which we pre-fill via
 * a query param so they don't have to retype the address).
 *
 * The Gateway always returns 200 with the same message regardless of
 * whether the email exists (anti-enumeration). So our success state is
 * "request was accepted" — not "we definitely sent something." Copy
 * mirrors that.
 */
export function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not request a reset code. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <TwoPaneLayout>
        <div>
          <h2 className="h4 mb-3">Check your email</h2>
          <p className="text-muted mb-4">
            If an AquaStar account exists for <strong>{email}</strong>, we just
            emailed a 6-digit reset code to it. The code expires shortly — once
            you have it, head to the next step to set a new password.
          </p>
          <button
            type="button"
            className="btn btn-primary w-100 mb-3"
            onClick={() =>
              navigate(`/reset-password?email=${encodeURIComponent(email)}`)
            }
          >
            I have the code — continue
          </button>
          <button
            type="button"
            className="btn btn-link w-100 small"
            onClick={() => {
              setSent(false);
              setError(null);
            }}
          >
            Try a different email
          </button>
          <hr className="my-4" />
          <p className="small text-muted text-center">
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      </TwoPaneLayout>
    );
  }

  return (
    <TwoPaneLayout>
      <form onSubmit={onSubmit} noValidate>
        <h2 className="h4 mb-3">Reset your password</h2>
        <p className="text-muted small mb-4">
          Enter the email address on your AquaStar account. We'll send a
          6-digit code you can use to set a new password.
        </p>

        <div className="mb-3">
          <label className="form-label small text-muted">Email</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            autoFocus
          />
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-100 mb-3"
          disabled={submitting || !email}
        >
          {submitting ? 'Sending…' : 'Send reset code'}
        </button>

        <hr className="my-4" />
        <div className="d-flex justify-content-between small">
          <Link to="/login">Back to sign in</Link>
          <Link to="/signup">Create an account</Link>
        </div>
      </form>
    </TwoPaneLayout>
  );
}
